import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit, UserX, UserCheck, Users } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const ROLE_OPTIONS = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'contador', label: 'Contador' },
  { value: 'recolector', label: 'Recolector' },
  { value: 'repartidor', label: 'Repartidor' },
  { value: 'distribucion', label: 'Distribución' },
  { value: 'call_center', label: 'Call Center' },
];

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ email: '', password: '', nombre_completo: '', telefono: '', role: 'admin' });
  const [editFormData, setEditFormData] = useState({ email: '', nombre_completo: '', telefono: '', role: '', password: '' });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data || []);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users', formData);
      toast.success('Usuario creado');
      fetchUsers();
      setDialogOpen(false);
      setFormData({ email: '', password: '', nombre_completo: '', telefono: '', role: 'admin' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setEditFormData({
      email: user.email || '',
      nombre_completo: user.nombre_completo || '',
      telefono: user.telefono || '',
      role: user.role || 'admin',
      password: '',
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {};
      if (editFormData.email && editFormData.email !== editingUser.email) payload.email = editFormData.email;
      if (editFormData.nombre_completo && editFormData.nombre_completo !== editingUser.nombre_completo) payload.nombre_completo = editFormData.nombre_completo;
      if (editFormData.telefono && editFormData.telefono !== editingUser.telefono) payload.telefono = editFormData.telefono;
      if (editFormData.role && editFormData.role !== editingUser.role) payload.role = editFormData.role;
      if (editFormData.password) payload.password = editFormData.password;

      if (Object.keys(payload).length === 0) {
        toast.info('No hay cambios para guardar');
        return;
      }

      await api.put(`/users/${editingUser.id}`, payload);
      toast.success('Usuario actualizado correctamente');
      fetchUsers();
      setEditDialogOpen(false);
      setEditingUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const toggleStatus = async (userId, currentStatus) => {
    try {
      await api.put(`/users/${userId}/toggle-status`, { activo: !currentStatus });
      toast.success('Estado actualizado');
      fetchUsers();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const getRoleBadge = (role) => {
    const variants = { super_admin: 'destructive', admin: 'default', contador: 'secondary', recolector: 'outline', repartidor: 'outline', cliente: 'secondary' };
    return <Badge variant={variants[role] || 'secondary'}>{role}</Badge>;
  };

  const RoleSelect = ({ value, onChange }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid="role-select"><SelectValue /></SelectTrigger>
      <SelectContent>
        {ROLE_OPTIONS.map((r) => (
          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6 pb-24">
      <div className="max-w-7xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl flex items-center"><Users className="mr-2" />Gestión de Usuarios</CardTitle>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild><Button data-testid="new-user-btn"><Plus className="mr-2 h-4 w-4" />Nuevo Usuario</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Nuevo Usuario</DialogTitle></DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="create-user-form">
                    <div><Label>Email *</Label><Input data-testid="create-email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required /></div>
                    <div><Label>Contraseña *</Label><Input data-testid="create-password" type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required /></div>
                    <div><Label>Nombre Completo *</Label><Input data-testid="create-name" value={formData.nombre_completo} onChange={(e) => setFormData({...formData, nombre_completo: e.target.value})} required /></div>
                    <div><Label>Teléfono *</Label><Input data-testid="create-phone" value={formData.telefono} onChange={(e) => setFormData({...formData, telefono: e.target.value})} required /></div>
                    <div><Label>Rol</Label><RoleSelect value={formData.role} onChange={(v) => setFormData({...formData, role: v})} /></div>
                    <div className="flex justify-end space-x-2">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                      <Button type="submit" data-testid="create-user-submit">Crear</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? <div className="text-center py-8">Cargando...</div> : (
              <Table data-testid="users-table">
                <TableHeader>
                  <TableRow><TableHead>Nombre</TableHead><TableHead>Email</TableHead><TableHead>Teléfono</TableHead><TableHead>Rol</TableHead><TableHead>Estado</TableHead><TableHead>Acciones</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center">No hay usuarios</TableCell></TableRow> : users.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell className="font-medium">{user.nombre_completo}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.telefono}</TableCell>
                      <TableCell data-testid={`user-role-${user.id}`}>{getRoleBadge(user.role)}</TableCell>
                      <TableCell>{user.activo ? <Badge variant="default">Activo</Badge> : <Badge variant="secondary">Inactivo</Badge>}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" data-testid={`edit-user-${user.id}`} onClick={() => openEditDialog(user)} title="Editar">
                            <Edit className="h-4 w-4 text-blue-500" />
                          </Button>
                          <Button variant="ghost" size="icon" data-testid={`toggle-user-${user.id}`} onClick={() => toggleStatus(user.id, user.activo)} title={user.activo ? 'Desactivar' : 'Activar'}>
                            {user.activo ? <UserX className="h-4 w-4 text-red-500" /> : <UserCheck className="h-4 w-4 text-green-500" />}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar Usuario</DialogTitle></DialogHeader>
          {editingUser && (
            <form onSubmit={handleEditSubmit} className="space-y-4" data-testid="edit-user-form">
              <div><Label>Email</Label><Input data-testid="edit-email" type="email" value={editFormData.email} onChange={(e) => setEditFormData({...editFormData, email: e.target.value})} /></div>
              <div><Label>Nueva Contraseña (dejar vacío para no cambiar)</Label><Input data-testid="edit-password" type="password" value={editFormData.password} onChange={(e) => setEditFormData({...editFormData, password: e.target.value})} placeholder="Sin cambios" /></div>
              <div><Label>Nombre Completo</Label><Input data-testid="edit-name" value={editFormData.nombre_completo} onChange={(e) => setEditFormData({...editFormData, nombre_completo: e.target.value})} /></div>
              <div><Label>Teléfono</Label><Input data-testid="edit-phone" value={editFormData.telefono} onChange={(e) => setEditFormData({...editFormData, telefono: e.target.value})} /></div>
              <div><Label>Rol</Label><RoleSelect value={editFormData.role} onChange={(v) => setEditFormData({...editFormData, role: v})} /></div>
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
                <Button type="submit" data-testid="edit-user-submit">Guardar Cambios</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersManagement;
